'use client';

import { useCallback, useSyncExternalStore } from 'react';

const KEY = 'jamsom:directory:recent';
const MAX = 6;
const EMPTY_ITEMS: readonly string[] = Object.freeze([]);

type StoreListener = () => void;

let snapshot: readonly string[] = EMPTY_ITEMS;
let hasReadStorage = false;
let isListeningToStorage = false;
const listeners = new Set<StoreListener>();

function areEqual(left: readonly string[], right: readonly string[]) {
  return left.length === right.length && left.every((item, index) => item === right[index]);
}

function parseItems(raw: string | null): readonly string[] {
  if (!raw) return EMPTY_ITEMS;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return EMPTY_ITEMS;

    const seen = new Set<string>();
    const items = parsed.reduce<string[]>((result, item) => {
      if (typeof item !== 'string') return result;
      const term = item.trim();
      const normalized = term.toLocaleLowerCase();
      if (!term || seen.has(normalized)) return result;
      seen.add(normalized);
      result.push(term);
      return result;
    }, []);

    return items.length ? Object.freeze(items) : EMPTY_ITEMS;
  } catch {
    return EMPTY_ITEMS;
  }
}

function updateSnapshot(next: readonly string[]) {
  if (areEqual(snapshot, next)) return;
  snapshot = next.length ? Object.freeze([...next]) : EMPTY_ITEMS;
  listeners.forEach((listener) => listener());
}

function getSnapshot() {
  if (typeof window === 'undefined') return EMPTY_ITEMS;

  if (!hasReadStorage) {
    hasReadStorage = true;
    try {
      updateSnapshot(parseItems(window.localStorage.getItem(KEY)));
    } catch {
      updateSnapshot(EMPTY_ITEMS);
    }
  }

  return snapshot;
}

const getServerSnapshot = () => EMPTY_ITEMS;

function onStorage(event: StorageEvent) {
  if (event.key !== KEY && event.key !== null) return;
  updateSnapshot(parseItems(event.key === KEY ? event.newValue : null));
}

function subscribe(listener: StoreListener) {
  listeners.add(listener);

  if (typeof window !== 'undefined' && !isListeningToStorage) {
    window.addEventListener('storage', onStorage);
    isListeningToStorage = true;
  }

  return () => {
    listeners.delete(listener);
    if (typeof window !== 'undefined' && isListeningToStorage && listeners.size === 0) {
      window.removeEventListener('storage', onStorage);
      isListeningToStorage = false;
    }
  };
}

function persist(next: readonly string[]) {
  updateSnapshot(next);
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Persistence can fail in private browsing or when the quota is exhausted.
  }
}

function getLimit(max: number) {
  return Number.isFinite(max) ? Math.max(0, Math.floor(max)) : MAX;
}

/**
 * Búsquedas recientes del directorio, persistidas en localStorage.
 * El store mantiene un snapshot estable para SSR e instancias simultáneas.
 */
export function useRecentSearches(max = MAX) {
  const items = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const limit = getLimit(max);

  const add = useCallback(
    (rawTerm: string) => {
      const term = rawTerm.trim();
      if (!term) return;

      const normalized = term.toLocaleLowerCase();
      const current = getSnapshot();
      const next = [term, ...current.filter((item) => item.toLocaleLowerCase() !== normalized)].slice(0, limit);
      persist(next);
    },
    [limit]
  );

  const clear = useCallback(() => {
    updateSnapshot(EMPTY_ITEMS);
    if (typeof window === 'undefined') return;

    try {
      window.localStorage.removeItem(KEY);
    } catch {
      // Clearing the in-memory snapshot is still useful if storage is unavailable.
    }
  }, []);

  return { items, add, clear };
}
